import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ManagementOption from "../../components/ManagementOption";
import { FaChartLine, FaClipboardCheck, FaFileInvoiceDollar, FaExchangeAlt, FaUserClock, FaPercentage, FaThumbsDown, FaChartPie, FaStickyNote, FaClock, FaChevronRight } from "react-icons/fa";
import { getCurrentUserEmployee } from "../../data/employeeData";
import "../Styles.css";
import "./Reports.css";

interface ReportsProps {
  allowedRoutes?: string[];
}

const Reports = (props: ReportsProps) => {
  const navigate = useNavigate();
  const [hasPipelinePermission, setHasPipelinePermission] = useState(false);
  const [hasCompletedJobsPermission, setHasCompletedJobsPermission] = useState(false);
  const [hasReportsPermission, setHasReportsPermission] = useState(false);
  const [hasMaterialMovementsPermission, setHasMaterialMovementsPermission] = useState(false);
  const [hasEmployeeTimePermission, setHasEmployeeTimePermission] = useState(false);
  const [hasQuoteConversionPermission, setHasQuoteConversionPermission] = useState(false);
  const [hasQuoteLostPermission, setHasQuoteLostPermission] = useState(false);
  const [hasQuoteJobSummaryPermission, setHasQuoteJobSummaryPermission] = useState(false);
  const [hasEmployeeOpenNotesPermission, setHasEmployeeOpenNotesPermission] = useState(false);

  /**
   * Checks if the user has permission to access a specific route.
   *
   * @param name - The name of the route to check permissions for.
   * @returns `true` if the user has permission to access the route or if the user has "Admin" permissions, otherwise `false`.
   */
  const canView = (name: string) => {
    const allowed = Array.isArray(props.allowedRoutes) ? props.allowedRoutes : [];
    return allowed.includes(name) || allowed.includes("Portal.Administrator");
  };

  useEffect(() => {
    const checkPermissions = async () => {
      const currentUser = await getCurrentUserEmployee();
      if (currentUser && currentUser.permissions) {
        const hasPipeline = currentUser.permissions.some(
          (p) => p.area === 'Reports' && p.permission === 'Pipeline' && p.assigned
        );
        // Its own role, with the administrator bypass the newer reports have,
        // matching the policy the API endpoint is secured with.
        const hasCompletedJobs = currentUser.permissions.some(
          (p) =>
            p.assigned &&
            ((p.area === 'Portal' && p.permission === 'Administrator') ||
              (p.area === 'Reports' && p.permission === 'CompletedJobs'))
        );
        const hasReports = currentUser.permissions.some(
          (p) => p.area === 'Reports' && p.permission === 'Invoices' && p.assigned
        );
        const hasMaterialMovements = currentUser.permissions.some(
          (p) => p.area === 'Reports' && p.permission === 'MaterialMovements' && p.assigned
        );
        const hasEmployeeTime = currentUser.permissions.some(
          (p) => p.area === 'Reports' && p.permission === 'EmployeeTime' && p.assigned
        );
        const hasQuoteConversion = currentUser.permissions.some(
          (p) => p.area === 'Reports' && p.permission === 'QuoteConversion' && p.assigned
        );
        // An administrator sees the tile without the report role being
        // assigned, matching the policy the API endpoint is trimmed with.
        const hasQuoteLost = currentUser.permissions.some(
          (p) =>
            p.assigned &&
            ((p.area === 'Portal' && p.permission === 'Administrator') ||
              (p.area === 'Reports' && p.permission === 'QuoteLost'))
        );
        // Same administrator bypass as the quote lost report above, matching
        // the policy the API endpoint is trimmed with.
        const hasQuoteJobSummary = currentUser.permissions.some(
          (p) =>
            p.assigned &&
            ((p.area === 'Portal' && p.permission === 'Administrator') ||
              (p.area === 'Reports' && p.permission === 'QuoteJobSummary'))
        );

        // Same administrator bypass as the reports above, matching the policy
        // the API endpoint is trimmed with.
        const hasEmployeeOpenNotes = currentUser.permissions.some(
          (p) =>
            p.assigned &&
            ((p.area === 'Portal' && p.permission === 'Administrator') ||
              (p.area === 'Reports' && p.permission === 'EmployeeOpenNotes'))
        );

        setHasPipelinePermission(hasPipeline);
        setHasCompletedJobsPermission(hasCompletedJobs);
        setHasReportsPermission(hasReports);
        setHasMaterialMovementsPermission(hasMaterialMovements);
        setHasEmployeeTimePermission(hasEmployeeTime);
        setHasQuoteConversionPermission(hasQuoteConversion);
        setHasQuoteLostPermission(hasQuoteLost);
        setHasQuoteJobSummaryPermission(hasQuoteJobSummary);
        setHasEmployeeOpenNotesPermission(hasEmployeeOpenNotes);
      }
    };
    checkPermissions();
  }, []);
  
  return (
    <div>
      <div style={{ padding: "1rem" }}>
        <div className="reports__header">
          <h1>Reports</h1>
          <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>View business intelligence and reporting</p>
        </div>
      </div>

      <div className="reports__promo-wrap">
        <button
          className="reports__promo"
          onClick={() => navigate("/report-schedules")}
        >
          <span className="reports__promo-icon">
            <FaClock />
          </span>
          <span className="reports__promo-text">
            <span className="reports__promo-title">Report Schedules</span>
            <span className="reports__promo-sub">
              Have any of the reports below emailed to you automatically - daily,
              weekly or monthly.
            </span>
          </span>
          <span className="reports__promo-go" aria-hidden="true">
            <FaChevronRight />
          </span>
        </button>
      </div>

      <div className="management__menu__container">
        <div className="management__menu__row">
          {hasPipelinePermission && (
            <div className="management__row__item">
              <ManagementOption
                onTitle="Pipeline"
                onSubTitle="View project and forecasting reports"
                onNavigation="/pipeline"
                onIcon={<FaChartLine />}
              />
            </div>
          )}
          {hasCompletedJobsPermission && (
            <div className="management__row__item">
              <ManagementOption
                onTitle="Completed Jobs"
                onSubTitle="The pipeline report for jobs marked as complete"
                onNavigation="/completed-jobs"
                onIcon={<FaClipboardCheck />}
              />
            </div>
          )}
          {hasReportsPermission && (
            <div className="management__row__item">
              <ManagementOption
                onTitle="Invoices"
                onSubTitle="View invoice reports by date range and department"
                onNavigation="/invoices-report"
                onIcon={<FaFileInvoiceDollar />}
              />
            </div>
          )}
          {hasMaterialMovementsPermission && (
            <div className="management__row__item">
              <ManagementOption
                onTitle="Material Movements"
                onSubTitle="Report on job cost movements between jobs"
                onNavigation="/material-movements"
                onIcon={<FaExchangeAlt />}
              />
            </div>
          )}
          {hasEmployeeTimePermission && (
            <div className="management__row__item">
              <ManagementOption
                onTitle="Employee Time"
                onSubTitle="View employee timesheet data by date range"
                onNavigation="/employee-time-report"
                onIcon={<FaUserClock />}
              />
            </div>
          )}
          {hasQuoteConversionPermission && (
            <div className="management__row__item">
              <ManagementOption
                onTitle="Quote Conversion"
                onSubTitle="View quote conversion rates by employee and date range"
                onNavigation="/quote-conversion-report"
                onIcon={<FaPercentage />}
              />
            </div>
          )}
          {hasQuoteJobSummaryPermission && (
            <div className="management__row__item">
              <ManagementOption
                onTitle="Quote & Job Summary"
                onSubTitle="Overview of quotes and jobs raised, by month and work type"
                onNavigation="/quote-job-summary"
                onIcon={<FaChartPie />}
              />
            </div>
          )}
          {hasEmployeeOpenNotesPermission && (
            <div className="management__row__item">
              <ManagementOption
                onTitle="Employee Open Notes"
                onSubTitle="Open notes outstanding against each employee"
                onNavigation="/employee-open-notes"
                onIcon={<FaStickyNote />}
              />
            </div>
          )}
          {hasQuoteLostPermission && (
            <div className="management__row__item">
              <ManagementOption
                onTitle="Quote Lost"
                onSubTitle="View lost quotes and their reasons by date range"
                onNavigation="/quote-lost-report"
                onIcon={<FaThumbsDown />}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;
